import { forwardRef, useState } from 'react';
import { BaseInputProps } from '../../types';
import styles from './PasswordInput.module.scss';

export interface PasswordInputProps extends Omit<BaseInputProps, 'type'> {
  /** Show password strength indicator */
  showStrength?: boolean;
  /** Current password strength (0-4) */
  strength?: number;
}

/**
 * PasswordInput Component
 * 
 * Password input with show/hide toggle and optional strength indicator
 * 
 * @example
 * ```tsx
 * <PasswordInput
 *   name="password"
 *   label="Password"
 *   placeholder="Enter password"
 *   required
 *   showStrength
 *   strength={passwordStrength}
 *   error={errors.password?.message}
 * />
 * ```
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      label,
      error,
      helperText,
      name,
      required = false,
      fullWidth = true,
      disabled,
      className,
      showStrength = false,
      strength = 0,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const inputId = `input-${name}`;
    const errorId = error ? `${inputId}-error` : undefined;
    const helperId = helperText ? `${inputId}-helper` : undefined;
    const hasError = Boolean(error);

    const togglePasswordVisibility = () => {
      setShowPassword((prev) => !prev);
    };

    const getStrengthLabel = (strength: number): string => {
      const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
      return labels[strength] || 'Very Weak';
    };

    const getStrengthColor = (strength: number): string => {
      const colors = [
        styles.strengthVeryWeak,
        styles.strengthWeak,
        styles.strengthFair,
        styles.strengthGood,
        styles.strengthStrong,
      ];
      return colors[strength] || colors[0];
    };

    return (
      <div className={`${styles.container} ${fullWidth ? styles.fullWidth : ''} ${className || ''}`}>
        {label && (
          <label htmlFor={inputId} className={styles.label}>
            {label}
            {required && <span className={styles.required} aria-label="required">*</span>}
          </label>
        )}

        <div className={styles.inputWrapper}>
          <input
            ref={ref}
            id={inputId}
            name={name}
            type={showPassword ? 'text' : 'password'}
            disabled={disabled}
            aria-invalid={hasError}
            aria-describedby={[errorId, helperId].filter(Boolean).join(' ') || undefined}
            aria-required={required}
            className={`${styles.input} ${hasError ? styles.inputError : ''}`}
            {...props}
          />

          <button
            type="button"
            onClick={togglePasswordVisibility}
            className={styles.toggleButton}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            tabIndex={-1}
          >
            {showPassword ? (
              <EyeOffIcon className={styles.icon} />
            ) : (
              <EyeIcon className={styles.icon} />
            )}
          </button>
        </div>

        {showStrength && strength > 0 && (
          <div className={styles.strengthIndicator} role="status" aria-live="polite">
            <div className={styles.strengthBars}>
              {[0, 1, 2, 3].map((level) => (
                <div
                  key={level}
                  className={`${styles.strengthBar} ${level < strength ? getStrengthColor(strength) : ''}`}
                />
              ))}
            </div>
            <span className={styles.strengthLabel}>{getStrengthLabel(strength)}</span>
          </div>
        )}

        {error && (
          <p id={errorId} className={styles.error} role="alert">
            {error}
          </p>
        )}

        {helperText && !error && (
          <p id={helperId} className={styles.helper}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';

// Simple SVG icons
const EyeIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);